"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var angular_1 = require("angular");
var lodash_1 = __importDefault(require("lodash"));
/**
 * Making certain requests in PARALLEL to containers-api can cause race-condition errors.
 *
 * E.g. the following can happen:
 *
 * 	POST new widget to empty container
 * 	PATCH to container
 * 	...
 *  POST response -> 200. container.definition.pages.widgets = [{ _id: '..', ...}]
 *  PATCH response -> 200. container.definition.pages.widgets = []
 *
 * So the POST request added a new widget.
 * But when the PATCH requests was processed, the container was saved with the version
 * of the container before the POST was processed.
 * This leads to errors and/or unexpected results,
 * e.g. widget failures, widgets appear in the ui that don't exist in containers-api, etc.
 *
 * (You can reproduce easily by rapidly copy/pasting the same widget with ctrl+v).
 * To prevent this, issue network requests to update the container in SERIAL.
 * We only need to throtlte requests at the container/page level.
 *
 * If this ends up being too slow, we may need to rethink the way we do saves.
 */
/**
 * We make a lot of patch requests.
 * Instead of a network request for each patch request,
 * send patch requests in batches.
 */
var PATCH_BATCH_INTERVAL_MS = 5000;
var WIDGET_METHODS = [
    'addWidget',
    'copyWidget',
    'deleteWidget'
];
var PAGE_METHODS = [
    'savePage',
    'addPage',
    'deletePage'
];
var PATCH_METHODS = [
    'patchContainer'
];
var NON_PATCH_METHODS = WIDGET_METHODS.concat(PAGE_METHODS);
var ALL_METHODS = WIDGET_METHODS.concat(PAGE_METHODS).concat(PATCH_METHODS);
(function (module) {
    module.factory('pr.rsContainersWrapper', [
        '$q',
        'rsContainers',
        'pr.navigationService',
        'pr.sequentialTaskQueue',
        function ($q, rsContainers, navigation, queue) {
            var service = lodash_1.default.cloneDeep(rsContainers);
            var containerId;
            var intervalId;
            var patch = {};
            var deferred = $q.defer();
            if (navigation.isEditor && !navigation.isExport) {
                limitNonPatchMethods();
                limitPatching();
            }
            else {
                // we should not edit a container loaded from export/public/preview view
                disableEditing();
            }
            return service;
            function disableEditing() {
                lodash_1.default.forEach(ALL_METHODS, function (method) {
                    service[method] = lodash_1.default.noop;
                });
            }
            /**
             * This makes requests run in serial instead of parallel
             */
            function limitNonPatchMethods() {
                lodash_1.default.forEach(NON_PATCH_METHODS, function (method) {
                    service[method] = function () {
                        var args = [];
                        for (var _i = 0; _i < arguments.length; _i++) {
                            args[_i] = arguments[_i];
                        }
                        return queue.add(function () { return rsContainers[method].apply(rsContainers, args); }).finally(function () {
                            if (!queue.anyPendingTasks() && !lodash_1.default.isEmpty(patch)) {
                                // normally patch requests run every 2s to prevent clogging the queue
                                // but if the queue is empty, then we can trigger any pending patch requests now.
                                makePatchRequest();
                            }
                        });
                    };
                });
            }
            /**
             * Patch requests do not need to be saved immediately (unless they are patches to the 'pages' property).
             * Also, patch requests always overwrite the previous patch, so they can be aggregated into one request.
             * So do not send a network request for each patch request as this will clog the queue.
             * Instead, issue patch requests in a batch every 10 seconds.
             */
            function limitPatching() {
                service.patchContainer = function (cId, newPatch) {
                    if (newPatch === void 0) { newPatch = {}; }
                    if (!containerId) {
                        containerId = cId;
                    }
                    // patch request always overwrite the previous patch
                    lodash_1.default.assign(patch, newPatch);
                    if (newPatch.pages || !queue.anyPendingTasks()) {
                        // if the patch is updating the 'pages' property, it needs to run ASAP
                        makePatchRequest();
                    }
                    else if (!intervalId) {
                        // otherwise, it can be updated with the next scheduled patch
                        intervalId = setInterval(function () {
                            makePatchRequest();
                        }, PATCH_BATCH_INTERVAL_MS);
                    }
                    // this will resolve/reject with the response from the upcoming patch request
                    return deferred.promise;
                };
            }
            function makePatchRequest() {
                if (lodash_1.default.isEmpty(patch)) {
                    return;
                }
                // run in this queue to guarantee that we do not issue parallel requests
                queue.add(function () {
                    if (lodash_1.default.isEmpty(patch)) {
                        return;
                    }
                    // future calls should not refer to old state
                    var toPatch = patch;
                    var toResolve = deferred;
                    // new calls to patchContainer will resolve/reject with this promise
                    deferred = $q.defer();
                    patch = {};
                    return $q.resolve().then(function () {
                        return rsContainers.patchContainer(containerId, toPatch);
                    }).then(toResolve.resolve, toResolve.reject);
                });
            }
        }
    ]);
})(angular_1.module('qualtrics.pr'));
